import { FC, useMemo, useState, useEffect, useRef } from 'react';

import classNames from 'classnames';

import { Modal, Spinner } from '../../../shared/ui';

const makeUrl = (url: string) => {
  if (!url) return '';

  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}${url}`;
};

interface IMacDeckGalleryModalProps {
  isOpen: boolean;
  deckName?: string;
  cards?: string[];
  onClose: () => void;
}

const DeckCardPreview: FC<{ src: string; index: number }> = ({ src, index }) => {
  const [loaded, setLoaded] = useState(false);
  const constructedSrc = makeUrl(src);
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (ref.current) {
      const resize = () => {
        const width = ref.current?.offsetWidth || 0;
        setHeight((width * 4) / 3);
      };
      resize();
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }
  }, []);

  return (
    <div
      ref={ref}
      style={{ height }}
      className="relative w-full overflow-hidden rounded-card bg-ink-subtle shadow-overlay"
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-center">
            <Spinner size="lg" className="text-ink-subtle" />
          </div>
        </div>
      )}
      <img
        src={constructedSrc}
        loading="lazy"
        alt={`card-${index}`}
        className={classNames(
          'absolute inset-0 h-full w-full object-cover bg-white rounded-card scale-[1.02] transition-opacity duration-300 ease-out',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
};

export const MacDeckGalleryModal: FC<IMacDeckGalleryModalProps> = ({ isOpen, deckName, cards, onClose }) => {
  const normalizedCards = useMemo(() => {
    if (!cards) return [];

    return cards.filter((card): card is string => typeof card === 'string' && card.length > 0);
  }, [cards]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={deckName ? `Колода «${deckName}»` : 'Колода'}
      description="Карты отображаются лицевой стороной. Вы можете пролистать галерею, чтобы оценить содержание колоды."
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {normalizedCards.length > 0 ? (
          normalizedCards.map((card, idx) => <DeckCardPreview src={card} index={idx} key={`${card}-${idx}`} />)
        ) : (
          <div className="col-span-full flex h-full min-h-[200px] items-center justify-center rounded-card bg-ink-muted text-center text-ui text-ink-muted">
            В этой колоде нет карт для отображения.
          </div>
        )}
      </div>
    </Modal>
  );
};
